import type { RemoteConfigKey } from '@/shared/firebase/types/remoteConfig.types';
import { REMOTE_CONFIG_TYPED_DEFAULTS } from '@/shared/firebase/constants/remoteConfig.constant';
import type { useRemoteConfigStore } from '@/shared/firebase/stores/remoteConfig.store';
import {
  isFeatureFlagObject,
  isFeatureFlagMultiKey,
  isPlainRecord,
} from '@/shared/firebase/utils/featureFlagSegmentation.util';

type RemoteConfigStore = ReturnType<typeof useRemoteConfigStore>;

function isNewContractFeatureFlagDefault(flagName: RemoteConfigKey): boolean {
  const defaultValue = REMOTE_CONFIG_TYPED_DEFAULTS[flagName];
  return isFeatureFlagObject(defaultValue) || isFeatureFlagMultiKey(defaultValue);
}

/**
 * Selecciona la sub-clave (`flagKey`, ej. país) dentro de un flag segmentado.
 * Si no hay `flagKey` o el valor no es un record, devuelve el valor tal cual.
 */
function resolveEffectiveFlagValue(
  rawValue: unknown,
  flagKey?: string | null
): unknown {
  if (flagKey != null && isPlainRecord(rawValue)) {
    return rawValue[flagKey];
  }

  return rawValue;
}

/**
 * Fuente única de verdad para decidir si un flag de ruta está habilitado.
 * Convive con ambos contratos de Remote Config:
 * - Nuevo contrato (`{ enabled }` o record segmentado `{ pais: { enabled } }`): `getFeatureFlag`
 * - Legacy (boolean u otros): `getRemoteConfigValue`
 *
 * `flagKey` selecciona una sub-clave (ej. país) dentro de un flag segmentado.
 */
export function resolveRouteFeatureFlagAllowed(
  remoteConfigStore: RemoteConfigStore,
  flagName: RemoteConfigKey,
  flagKey?: string | null
): boolean {
  if (isNewContractFeatureFlagDefault(flagName)) {
    const rawValue = remoteConfigStore.getFeatureFlag(flagName).value;
    const effectiveValue = resolveEffectiveFlagValue(rawValue, flagKey);
    return isFeatureFlagObject(effectiveValue) && effectiveValue.enabled;
  }

  const rawValue = remoteConfigStore.getRemoteConfigValue(flagName).value;
  const effectiveValue = resolveEffectiveFlagValue(rawValue, flagKey);
  return Boolean(effectiveValue);
}
